import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { Loader2, Upload, ExternalLink } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { optimizeImage, validateImageFile } from '../utils/imageOptimizer';
import type { Release } from '../types/database';

const releaseSchema = z.object({
  title: z.string().min(1, 'Title is required').max(100, 'Title too long'),
  type: z.enum(['Single', 'LP', 'EP', 'Album']),
  image_url: z.string().min(1, 'Image is required'),
  release_year: z.union([
    z.number().int().min(1900).max(2100).optional(),
    z.literal('')
  ]).transform(val => val === '' ? null : val),
  streaming_url: z.union([
    z.string().url('Please enter a valid URL').refine(url => 
      url.includes('spotify.com') || url.includes('soundcloud.com'), 
      'Must be a Spotify or SoundCloud URL'
    ),
    z.string().length(0)
  ]).optional().transform(val => val === '' ? null : val),
});

type ReleaseFormData = z.infer<typeof releaseSchema>;

interface ReleaseFormProps {
  bandId: string;
  release?: Release;
  onSuccess: () => void;
  onCancel: () => void;
}

export function ReleaseForm({ bandId, release, onSuccess, onCancel }: ReleaseFormProps) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm<ReleaseFormData>({
    resolver: zodResolver(releaseSchema),
    defaultValues: {
      title: release?.title || '',
      type: release?.type || 'Single',
      image_url: release?.image_url || '',
      release_year: release?.release_year || new Date().getFullYear(),
      streaming_url: release?.streaming_url || '',
    },
  });

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);
      setError('');

      let file = event.target.files?.[0];
      if (!file) return;

      await validateImageFile(file);

      // Optimize image before upload
      const optimized = await optimizeImage(file, {
        maxWidth: 500,
        maxHeight: 500,
        quality: 0.85,
        format: 'image/webp',
      });
      file = optimized.file;

      // Generate unique file name
      const fileName = `${uuidv4().replace(/-/g, '')}.webp`;

      // Delete old image if updating
      if (release?.image_url) {
        const oldFileName = release.image_url.split('/').pop();
        if (oldFileName) {
          await supabase.storage
            .from('releases')
            .remove([oldFileName]);
        }
      }

      // Upload file to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('releases')
        .upload(fileName, file, {
          cacheControl: '31536000',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('releases')
        .getPublicUrl(fileName);

      setValue('image_url', publicUrl);
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (data: ReleaseFormData) => {
    if (submitting) return;
    
    try {
      setSubmitting(true);
      setError('');

      if (release) {
        // Update existing release
        const { error: updateError } = await supabase
          .from('releases')
          .update({
            title: data.title,
            type: data.type,
            image_url: data.image_url,
            streaming_url: data.streaming_url,
            release_year: data.release_year,
            updated_at: new Date().toISOString(),
          })
          .eq('id', release.id);

        if (updateError) throw updateError;
      } else {
        // Create new release
        const { error: insertError } = await supabase
          .from('releases')
          .insert({
            band_id: bandId,
            title: data.title,
            type: data.type,
            image_url: data.image_url,
            streaming_url: data.streaming_url,
            release_year: data.release_year,
          });

        if (insertError) throw insertError;
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving release:', err);
      setError(err instanceof Error ? err.message : 'Failed to save release');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {error && (
        <div className="bg-red-500/10 text-red-500 p-4 rounded-lg text-center">
          {error}
        </div>
      )}

      <div>
        <label htmlFor="title" className="block text-sm font-medium text-light/80 mb-2">
          Release Title
        </label>
        <input
          type="text"
          id="title"
          {...register('title')}
          className="w-full px-4 py-2 bg-dark border border-gray-custom/20 rounded-lg text-light placeholder-light/40 focus:border-gray-custom/40 focus:outline-none"
          placeholder="Enter release title..."
        />
        {errors.title && (
          <p className="mt-1 text-sm text-red-500">{errors.title.message}</p>
        )}
      </div>

      <div>
        <label htmlFor="type" className="block text-sm font-medium text-light/80 mb-2">
          Release Type
        </label>
        <select
          id="type"
          {...register('type')}
          className="w-full px-4 py-2 bg-dark border border-gray-custom/20 rounded-lg text-light focus:border-gray-custom/40 focus:outline-none"
        >
          <option value="Single">Single</option>
          <option value="EP">EP</option>
          <option value="LP">LP</option>
          <option value="Album">Album</option>
        </select>
        {errors.type && (
          <p className="mt-1 text-sm text-red-500">{errors.type.message}</p>
        )}
      </div>

      <div>
        <label htmlFor="release_year" className="block text-sm font-medium text-light/80 mb-2">
          Release Year
        </label>
        <input
          type="number"
          id="release_year"
          min="1900"
          max="2100"
          {...register('release_year', { valueAsNumber: true })}
          className="w-full px-4 py-2 bg-dark border border-gray-custom/20 rounded-lg text-light focus:border-gray-custom/40 focus:outline-none"
          placeholder={new Date().getFullYear().toString()}
        />
        {errors.release_year && (
          <p className="mt-1 text-sm text-red-500">{errors.release_year.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-light/80 mb-2">
          Cover Image (1:1 ratio recommended)
        </label>
        <div className="flex items-center space-x-4">
          <div className="w-24 h-24 bg-gray-custom/10 flex items-center justify-center overflow-hidden rounded-lg">
            {watch('image_url') ? (
              <img
                src={watch('image_url')}
                alt="Release cover"
                className="w-full h-full object-cover"
               loading="lazy" />
            ) : (
              <Upload className="w-8 h-8 text-light/60" />
            )}
          </div>
          <div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              accept="image/*"
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="btn-outline"
            >
              {uploading ? 'Uploading...' : 'Upload Image'}
            </button>
          </div>
        </div>
        {errors.image_url && (
          <p className="mt-1 text-sm text-red-500">{errors.image_url.message}</p>
        )}
      </div>

      <div>
        <label htmlFor="streaming_url" className="block text-sm font-medium text-light/80 mb-2">
          Streaming URL (optional - Spotify or SoundCloud)
        </label>
        <input
          type="url"
          id="streaming_url"
          {...register('streaming_url')}
          className="w-full px-4 py-2 bg-dark border border-gray-custom/20 rounded-lg text-light placeholder-light/40 focus:border-gray-custom/40 focus:outline-none"
          placeholder="https://open.spotify.com/album/... or https://soundcloud.com/artist/album"
        />
        {errors.streaming_url && (
          <p className="mt-1 text-sm text-red-500">{errors.streaming_url.message}</p>
        )}
      </div>

      <div className="flex flex-col md:flex-row justify-end md:space-x-4">
        <button
          type="button"
          onClick={onCancel}
          className="btn-outline order-2 mt-6 md:mt-0"
          disabled={submitting}
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          className="btn-secondary md:order-2"
          disabled={submitting}
        >
          {submitting ? (
            <span className="flex items-center space-x-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>{release ? 'Updating Release...' : 'Adding Release...'}</span>
            </span>
          ) : (
            release ? 'Update Release' : 'Add Release'
          )}
        </button>
      </div>
    </form>
  );
}